"use client";

import { useEffect, useState, useRef } from "react";
import Link from "next/link";
import { Menu, X, ChevronDown, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { Container } from "@/components/ui/container";
import { Button } from "@/components/ui/button";
import { Icon } from "@/components/ui/icon";
import { ThemeToggle } from "./theme-toggle";
import { NAV_GROUPS, type NavGroup } from "@/lib/data/nav";

export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const closeTimer = useRef<number | null>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpenGroup(null);
        setMobileOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const handleEnter = (label: string) => {
    if (closeTimer.current) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setOpenGroup(label);
  };

  const handleLeave = () => {
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => setOpenGroup(null), 120);
  };

  return (
    <header
      className={cn(
        "fixed top-0 inset-x-0 z-50 transition-all duration-500",
        scrolled ? "py-2.5" : "py-4",
      )}
    >
      <Container>
        <nav
          className={cn(
            "flex items-center justify-between gap-4 rounded-full px-4 sm:px-6 transition-all duration-500",
            scrolled ? "glass py-2.5" : "py-2",
          )}
        >
          {/* Logo */}
          <Link href="/" className="flex items-center gap-2.5 shrink-0 group">
            <span className="relative grid place-items-center h-9 w-9 rounded-xl bg-gradient-to-br from-[var(--color-crimson)] to-[var(--color-purple)] shadow-lg shadow-[var(--color-crimson)/30]">
              <span className="absolute inset-0 rounded-xl ring-1 ring-white/30" />
              <span className="font-bold text-white text-lg leading-none tracking-tight">X2</span>
            </span>
            <span className="flex flex-col leading-none">
              <span className="text-[1.05rem] font-bold tracking-tight">Perception X2</span>
              <span className="text-[0.6rem] uppercase tracking-[0.3em] text-[rgb(var(--fg-subtle))]">
                by CryptoMize
              </span>
            </span>
          </Link>

          {/* Desktop mega-menu */}
          <div className="hidden lg:flex items-center gap-1">
            {NAV_GROUPS.map((group) => (
              <div
                key={group.label}
                className="relative"
                onMouseEnter={() => handleEnter(group.label)}
                onMouseLeave={handleLeave}
              >
                <button
                  type="button"
                  className={cn(
                    "px-3.5 py-2 rounded-full text-sm font-medium inline-flex items-center gap-1.5 transition-colors",
                    openGroup === group.label
                      ? "text-[rgb(var(--fg))] bg-[rgb(var(--border)/0.12)]"
                      : "text-[rgb(var(--fg-muted))] hover:text-[rgb(var(--fg))] hover:bg-[rgb(var(--border)/0.08)]",
                  )}
                  aria-expanded={openGroup === group.label}
                  aria-haspopup="true"
                >
                  {group.label}
                  <ChevronDown
                    className={cn(
                      "h-3.5 w-3.5 transition-transform duration-300",
                      openGroup === group.label && "rotate-180",
                    )}
                  />
                </button>
              </div>
            ))}
          </div>

            {/* Actions */}
            <div className="flex items-center gap-2">
              <ThemeToggle />
              <Link
                href="#contact"
                className="hidden sm:inline-flex btn-primary-gradient h-10 px-5 items-center gap-1.5 rounded-full font-semibold text-sm"
              >
                Request Demo
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            <button
              type="button"
              onClick={() => setMobileOpen((v) => !v)}
              aria-label="Menu"
              className="lg:hidden grid place-items-center h-10 w-10 rounded-full glass"
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </nav>

        {/* Mega-menu panel (desktop) */}
        {openGroup && (
          <div
            className="hidden lg:block mt-2 animate-[reveal-up_0.25s_ease]"
            onMouseEnter={() => handleEnter(openGroup)}
            onMouseLeave={handleLeave}
          >
            <MegaPanel group={NAV_GROUPS.find((g) => g.label === openGroup)!} />
          </div>
        )}

        {/* Mobile menu (full screen sheet) */}
        {mobileOpen && (
          <div className="lg:hidden mt-2 glass rounded-2xl p-4 animate-[reveal-up_0.3s_ease] max-h-[calc(100vh-6rem)] overflow-y-auto">
            <MobileMenu onClose={() => setMobileOpen(false)} />
          </div>
        )}
      </Container>
    </header>
  );
}

function MegaPanel({ group }: { group: NavGroup }) {
  const totalItems = group.columns.reduce((s, c) => s + c.items.length, 0);
  const hasFeatured = !!group.featured;

  return (
    <div className="glass rounded-2xl p-6 ring-glow">
      <div
        className="grid gap-6"
        style={{
          gridTemplateColumns: hasFeatured
            ? `repeat(${group.columns.length}, minmax(0, 1fr)) 280px`
            : `repeat(${group.columns.length}, minmax(0, 1fr))`,
        }}
      >
        {group.columns.map((col) => (
          <div key={col.title}>
            <div className="text-[0.65rem] uppercase tracking-[0.22em] font-semibold text-[rgb(var(--fg-subtle))] mb-3 px-1">
              {col.title}
            </div>
            <div className="grid gap-1">
              {col.items.map((item) => (
                <Link
                  key={item.href}
                  href={item.href}
                  className="group flex items-start gap-3 rounded-xl px-3 py-2.5 hover:bg-[rgb(var(--border)/0.08)] transition-colors"
                >
                  <span
                    className="grid place-items-center h-8 w-8 rounded-lg shrink-0 mt-0.5 transition-transform duration-300 group-hover:scale-110"
                    style={{
                      background: `linear-gradient(135deg, color-mix(in oklch, var(--color-crimson) 18%, transparent), color-mix(in oklch, var(--color-purple) 12%, transparent))`,
                      boxShadow: "inset 0 0 0 1px rgb(var(--border) / 0.15)",
                    }}
                  >
                    <Icon
                      name={item.icon}
                      className="h-4 w-4 text-[var(--color-crimson)]"
                    />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold text-[rgb(var(--fg))] leading-tight">
                      {item.label}
                    </span>
                    {item.description && (
                      <span className="block text-[0.78rem] text-[rgb(var(--fg-muted))] mt-0.5 leading-snug">
                        {item.description}
                      </span>
                    )}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        ))}

        {/* Featured card */}
        {group.featured && (
          <div
            className="card-glow relative rounded-2xl p-5 overflow-hidden flex flex-col justify-between"
            style={{
              background: `linear-gradient(135deg, color-mix(in oklch, ${group.featured.accent} 14%, transparent), color-mix(in oklch, ${group.featured.accent} 4%, transparent))`,
              boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${group.featured.accent} 30%, transparent)`,
            }}
          >
            <div
              className="absolute -top-12 -right-12 h-32 w-32 rounded-full blur-3xl opacity-30 pointer-events-none"
              style={{ background: group.featured.accent }}
            />
            <div className="relative">
              <div className="text-[0.65rem] uppercase tracking-[0.22em] font-semibold text-[rgb(var(--fg-subtle))] mb-2">
                Featured
              </div>
              <h3 className="text-base font-bold leading-tight mb-1.5">
                {group.featured.title}
              </h3>
              <p className="text-[0.82rem] text-[rgb(var(--fg-muted))] leading-relaxed">
                {group.featured.description}
              </p>
            </div>
            <Link
              href={group.featured.href}
              className="relative inline-flex items-center gap-1.5 text-sm font-semibold mt-4 group/cta"
              style={{ color: group.featured.accent }}
            >
              {group.featured.cta}
              <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover/cta:translate-x-0.5" />
            </Link>
          </div>
        )}
      </div>

      {/* Panel footer */}
      <div className="mt-5 pt-4 border-t border-[rgb(var(--border)/0.15)] flex items-center justify-between text-[0.78rem]">
        <span className="text-[rgb(var(--fg-subtle))]">
          {totalItems} {totalItems === 1 ? "page" : "pages"} in {group.label}
        </span>
        <Link
          href={group.href}
          className="inline-flex items-center gap-1 font-semibold text-[rgb(var(--fg))] hover:text-[var(--color-crimson)] transition-colors"
        >
          View all {group.label}
          <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>
    </div>
  );
}

function MobileMenu({ onClose }: { onClose: () => void }) {
  const [openSection, setOpenSection] = useState<string | null>(null);

  return (
    <div className="grid gap-2">
      {NAV_GROUPS.map((group) => (
        <div key={group.label} className="rounded-xl overflow-hidden">
          <button
            type="button"
            onClick={() => setOpenSection(openSection === group.label ? null : group.label)}
            className="w-full flex items-center justify-between px-4 py-3 hover:bg-[rgb(var(--border)/0.08)] transition-colors"
            aria-expanded={openSection === group.label}
          >
            <span className="flex items-center gap-2.5">
              <Icon name={group.icon} className="h-4 w-4 text-[var(--color-crimson)]" />
              <span className="font-semibold text-sm">{group.label}</span>
            </span>
            <ChevronDown
              className={cn(
                "h-4 w-4 transition-transform duration-300",
                openSection === group.label && "rotate-180",
              )}
            />
          </button>
          {openSection === group.label && (
            <div className="px-3 pb-3 grid gap-1 animate-[reveal-up_0.2s_ease]">
              {group.columns.flatMap((c) => c.items).map((item) => (
                <Link
                  key={item.href}
                  href={item.href}
                  onClick={onClose}
                  className="flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-[rgb(var(--border)/0.08)] text-sm"
                >
                  <Icon name={item.icon} className="h-3.5 w-3.5 text-[rgb(var(--fg-subtle))]" />
                  <span>{item.label}</span>
                </Link>
              ))}
            </div>
          )}
        </div>
      ))}

      <div className="mt-3 pt-3 border-t border-[rgb(var(--border)/0.15)] grid gap-2">
        <Link
          href="/company/about"
          onClick={onClose}
          className="px-4 py-3 rounded-xl text-sm font-medium hover:bg-[rgb(var(--border)/0.08)]"
        >
          Company
        </Link>
        <Link
          href="/contact"
          onClick={onClose}
          className="px-4 py-3 rounded-xl text-sm font-medium hover:bg-[rgb(var(--border)/0.08)]"
        >
          Contact
        </Link>
        <Button
          variant="primary"
          size="md"
          className="w-full"
          onClick={() => {
            onClose();
            window.location.href = "/platform/demo";
          }}
        >
          Request Demo
          <ArrowRight className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}
